// Avatar renderer: Y2K pixel chibis built from layered sprites (see chibi.ts), cached per
// look and animation frame, and drawn with nearest-neighbour scaling.
import { EYE_COLORS, HAIR_COLORS, HAIR_STYLES, ITEM_BY_ID, DEFAULT_LOOK, type Item, type Look } from "../shared/catalog";
import { buildChibi, buildHairItem, buildItem, FOOT_Y, SH, SW, type ChibiLook, type Frame, type Part } from "./chibi";
import type { Bitmap } from "./pixel";

export interface Pose {
  dir: number; // 0 +x (down-right), 1 +y (down-left), 2 -x (up-left), 3 -y (up-right)
  walk: number; // walk cycle phase, 0 when standing
  emote: string | null; // an emote id
  t: number; // time in ms, for animation
}

/** World pixels per sprite pixel. */
export const SPRITE_SCALE = 2;

const part = (id: string | null, fallback?: string): Part | null => {
  const it: Item | undefined = (id && ITEM_BY_ID[id]) || (fallback ? ITEM_BY_ID[fallback] : undefined);
  return it ? { kind: it.kind, color: it.color, accent: it.accent, accent2: it.accent2 } : null;
};

export function toChibi(look: Look): ChibiLook {
  return {
    skin: look.skin,
    hairColor: HAIR_COLORS[look.hair] ?? HAIR_COLORS[0],
    hairStyle: HAIR_STYLES[look.hairStyle] ?? HAIR_STYLES[0],
    eyeColor: EYE_COLORS[look.eyes] ?? EYE_COLORS[0],
    top: part(look.top, DEFAULT_LOOK.top)!,
    bottom: part(look.bottom, DEFAULT_LOOK.bottom)!,
    shoes: part(look.shoes, DEFAULT_LOOK.shoes)!,
    hat: part(look.hat),
    face: part(look.glasses),
    ears: part(look.acc),
  };
}

// ---------- sprite cache ----------

const cache = new Map<string, HTMLCanvasElement>();

function toCanvas(b: Bitmap): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = b.w;
  c.height = b.h;
  c.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(b.data), b.w, b.h), 0, 0);
  return c;
}

export function sprite(look: Look, f: Frame, flip: boolean): HTMLCanvasElement {
  const key = `${look.skin}|${look.hair}|${look.hairStyle}|${look.eyes}|${look.top}|${look.bottom}|${look.shoes}|${look.hat}|${look.glasses}|${look.acc}|${+f.back}${f.step}${f.arms}${f.wave ?? 0}${f.bob ?? 0}${+flip}`;
  let c = cache.get(key);
  if (!c) {
    if (cache.size > 800) cache.clear();
    c = toCanvas(buildChibi(toChibi(look), f, flip));
    cache.set(key, c);
  }
  return c;
}

export function frameFor(pose: Pose): { f: Frame; flip: boolean } {
  const dir = pose.emote === "spin" ? Math.floor(pose.t / 140) % 4 : pose.dir;
  const back = dir === 2 || dir === 3;
  const flip = dir === 1 || dir === 2;
  const f: Frame = { back, step: 0, arms: "down" };
  if (pose.emote === "cheer") {
    f.arms = "up";
    f.wave = (Math.floor(pose.t / 200) % 2) as 0 | 1;
    f.bob = f.wave ? -1 : 0;
  } else if (pose.emote === "jump") {
    f.arms = (pose.t % 600) / 600 < 0.5 ? "up" : "down"; // the lift itself is applied in drawAvatar
  } else if (pose.emote === "dance") {
    const beat = Math.floor(pose.t / 260) % 4;
    f.arms = beat % 2 ? "up" : "wave";
    f.wave = beat < 2 ? 0 : 1;
    f.step = beat === 1 ? 1 : beat === 3 ? -1 : 0;
    f.bob = beat % 2 ? -2 : 0;
  } else if (pose.emote === "wave" && !back) {
    f.arms = "wave";
    f.wave = Math.floor(pose.t / 180) % 2 as 0 | 1;
  } else if (pose.walk) {
    // 4 frames: step, pass, other step, pass; the body rises as the legs pass
    const k = Math.floor((pose.walk % 1) * 4);
    f.step = k === 0 ? 1 : k === 2 ? -1 : 0;
    f.bob = k % 2 ? -1 : 0;
  }
  return { f, flip };
}

// ---------- aura particles ----------

const PARTICLES: Record<string, string[]> = {
  sparkle: ["..x..", "..x..", "xxwxx", "..x..", "..x.."],
  hearts: [".x.x.", "xwxxx", "xxxxx", ".xxx.", "..x.."],
};
const particleCache = new Map<string, HTMLCanvasElement>();
function particle(kind: string, color: string) {
  const key = kind + color;
  let c = particleCache.get(key);
  if (!c) {
    const rows = PARTICLES[kind] ?? PARTICLES.sparkle;
    c = document.createElement("canvas");
    c.width = c.height = 5;
    const g = c.getContext("2d")!;
    rows.forEach((r, y) => [...r].forEach((ch, x) => {
      if (ch === ".") return;
      g.fillStyle = ch === "w" ? "#ffffff" : color;
      g.fillRect(x, y, 1, 1);
    }));
    particleCache.set(key, c);
  }
  return c;
}

/** Draws an avatar whose feet are at (0,0). About 100px tall. */
export function drawAvatar(ctx: CanvasRenderingContext2D, look: Look, pose: Pose) {
  const { f, flip } = frameFor(pose);
  const img = sprite(look, f, flip);
  const s = SPRITE_SCALE;
  ctx.save();
  // pixel shadow
  ctx.fillStyle = "rgba(20,10,40,.28)";
  ctx.fillRect(-12 * s, -1 * s, 24 * s, 2 * s);
  ctx.fillRect(-9 * s, -2 * s, 18 * s, 4 * s);
  ctx.imageSmoothingEnabled = false;
  const lift = pose.emote === "jump" ? Math.round(Math.sin(((pose.t % 600) / 600) * Math.PI) * 8) * s : 0;
  ctx.drawImage(img, Math.round(-SW / 2) * s, -(FOOT_Y + 1) * s - lift, SW * s, SH * s);

  const aura = look.aura ? ITEM_BY_ID[look.aura] : null;
  if (aura) {
    const p = particle(aura.kind, aura.color);
    for (let i = 0; i < 6; i++) {
      const a = pose.t / 900 + (i * Math.PI) / 3;
      const x = Math.round(Math.cos(a) * 22) * s;
      const y = Math.round(-26 + Math.sin(a * 1.7) * 18) * s;
      ctx.globalAlpha = 0.55 + 0.45 * Math.sin(pose.t / 300 + i);
      const d = (Math.sin(pose.t / 200 + i) > 0.6 ? 7 : 5) * s;
      ctx.drawImage(p, x - d / 2, y - d / 2, d, d);
    }
  }
  ctx.restore();
}

/** Standalone avatar preview for the wardrobe and shop: a pixelated canvas. */
export function avatarThumb(look: Look, size = 96): HTMLCanvasElement {
  const img = sprite(look, { back: false, step: 0, arms: "down" }, false);
  const c = document.createElement("canvas");
  c.width = SW;
  c.height = SH;
  c.getContext("2d")!.drawImage(img, 0, 0);
  c.className = "pix";
  c.style.height = size + "px";
  c.style.width = Math.round((size * SW) / SH) + "px";
  return c;
}

/** Just the item, no character: what shop and closet tiles show. */
export function itemThumb(id: string, size = 96): HTMLCanvasElement {
  const it = ITEM_BY_ID[id];
  let c: HTMLCanvasElement;
  if (it.slot === "aura") {
    // three particles in a little cluster
    const p = particle(it.kind, it.color);
    c = document.createElement("canvas");
    c.width = 15; c.height = 13;
    const g = c.getContext("2d")!;
    g.drawImage(p, 0, 0); g.drawImage(p, 9, 2); g.drawImage(p, 4, 8);
  } else {
    const b = buildItem(it.slot, { kind: it.kind, color: it.color, accent: it.accent, accent2: it.accent2 });
    c = b ? toCanvas(b) : document.createElement("canvas");
  }
  // whole-pixel scaling keeps the pixel art crisp
  const k = Math.max(1, Math.min(6, Math.floor(Math.min(size / c.width, (size * 0.8) / c.height))));
  c.className = "pix";
  c.style.width = c.width * k + "px";
  c.style.height = c.height * k + "px";
  return c;
}

/** A hairstyle on a mannequin head, in your hair color: what hair tiles show. */
export function hairThumb(style: number, color: number, size = 96): HTMLCanvasElement {
  const b = buildHairItem(HAIR_STYLES[style] ?? HAIR_STYLES[0], HAIR_COLORS[color] ?? HAIR_COLORS[0]);
  const c = b ? toCanvas(b) : document.createElement("canvas");
  const k = Math.max(1, Math.min(6, Math.floor(Math.min(size / c.width, (size * 0.8) / c.height))));
  c.className = "pix";
  c.style.width = c.width * k + "px";
  c.style.height = c.height * k + "px";
  return c;
}
